import { Capacitor } from '@capacitor/core'
import { jwtDecode } from 'jwt-decode'

import type { UserInfo } from '@/stores/authStore'
import { appAuth } from './appAuth'
import { webAuth } from './webAuth'

export interface AuthCheckResult {
  success: boolean
  user?: UserInfo
}

const isApp = Capacitor.isNativePlatform()

const toUserInfo = (source: any): UserInfo => ({
  userId: source?.userId || source?.sub || '',
  merchantId: source?.merchantId || '',
  name: source?.username || source?.name || source?.sub || '',
  roles: Array.isArray(source?.roles) ? source.roles.map(String) : [],
})

export const useAuthAdapter = () => {
  const checkAuth = async (): Promise<AuthCheckResult> => {
    if (!isApp) {
      return webAuth.checkAuth()
    }

    const success = await appAuth.checkAuth()
    const savedUser = success ? await appAuth.getUser() : null
    const token = success && !savedUser ? await appAuth.getAccessToken() : null
    const user = savedUser || (token ? toUserInfo(jwtDecode(token)) : null)

    return user
      ? { success: true, user }
      : { success }
  }

  const login = async () => {
    return isApp
      ? appAuth.login()
      : webAuth.login()
  }

  const logout = async () => {
    return isApp
      ? appAuth.logout()
      : webAuth.logout()
  }

  const setUser = async (user: UserInfo) => {
    if (isApp) {
      return appAuth.setUser(user)
    }
  }

  const getUser = async () => {
    if (isApp) {
      return appAuth.getUser()
    }
    return null
  }

  const clearPersistence = async () => {
    if (isApp) {
      return appAuth.clear()
    }
  }

  return {
    checkAuth,
    login,
    logout,
    setUser,
    getUser,
    clearPersistence,
  }
}
